import { PictureAsPdf } from "@mui/icons-material";
import { Box, Button, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import BgBox from "../../../components/BgBox";
import CtrlOutTxtField from "../../../components/CtrlOutTxtField";
import CustomDropDown from "../../../components/CustomDropDown";
import LoadingScreen from "../../../components/loadingScreen";
import StyledTableContainer from "../../../components/StyledTableContainer";
import { customAlert } from "../../../components/notify";
import QueryKey from "../../../QueryKey";
import { getAllCustWPgSch } from "../../../services/cust-service";
import { getCustLedger } from "../../../services/shipmt-service";
import { custLedgerPdf } from "../../../services/pdf-service";
import { useAuthContext } from "../../../context/AuthContext";
import { capFirstLetter, formatPymtType, getLast30Days } from "../../../utils/format";

const BREAKUP_LABELS = {
    aUnloadingCooli: "Addition - Unloading Cooli",
    aLoadingCooli: "Addition - Loading Cooli",
    aHalting: "Addition - Halting",
    aDemmurage: "Addition - Demmurage",
    aOthers: "Addition - Others",
    dTds: "Deduction - TDS",
    dCommission: "Deduction - Commission",
    dPenalty: "Deduction - Penalty",
    dClaim: "Deduction - Claim",
    dLoadingCooli: "Deduction - Loading Cooli",
    dUnloadingCooli: "Deduction - Unloading Cooli",
    dOthers: "Deduction - Others",
};

function fmtAmt(n) {
    if (!n) return "";
    return Number(n).toLocaleString('en-IN');
}

// Ledger balance with DR/CR notation: positive = customer owes us (DR).
function fmtBal(n) {
    const v = Number(n || 0);
    if (v === 0) return "0";
    if (v > 0) return `${v.toLocaleString('en-IN')} DR`;
    return `(-)${Math.abs(v).toLocaleString('en-IN')} CR`;
}

function fmtDate(d) {
    return new Date(d).toLocaleDateString('en-GB');
}

// Period of transactions must be within 6 months.
function isRangeOverSixMonths(fromDate, toDate) {
    const from = new Date(fromDate);
    const limit = new Date(from);
    limit.setMonth(limit.getMonth() + 6);
    return new Date(toDate) > limit;
}

function CustLedgerPage() {
    const { t } = useTranslation();
    const { setLoadingScreen } = useAuthContext();
    const { today, priorDate } = getLast30Days();

    const [searchParams, setSearchParams] = useSearchParams();
    const custId = searchParams.get("custId") || "";
    const fromDate = searchParams.get("fromDate") || priorDate;
    const toDate = searchParams.get("toDate") || today;

    function setParam(name, value) {
        setSearchParams(prev => {
            prev.set(name, value)
            return prev
        }, { replace: true })
    }

    const rangeInvalid = !fromDate || !toDate || new Date(fromDate) > new Date(toDate);
    const rangeTooLong = !rangeInvalid && isRangeOverSixMonths(fromDate, toDate);
    const canFetch = Boolean(custId) && !rangeInvalid && !rangeTooLong;

    const { data: custRows } = useQuery(
        [QueryKey.custList, "ledgerDD"],
        () => getAllCustWPgSch({ limit: 1000, pageNumber: 1, searchKeyWord: "" }));

    const custDDArr = (custRows?.data || []).map((c) => ({
        value: String(c.custId),
        label: `${capFirstLetter(c.fName)} ${capFirstLetter(c.lName)}`,
    }));

    const { isLoading, isError, error, data: ledger } = useQuery(
        [QueryKey.custLedger, custId, fromDate, toDate],
        () => getCustLedger({ custId, fromDate, toDate }),
        { enabled: canFetch, keepPreviousData: true });

    if (isError) {
        customAlert(error);
        return <h2>Something went wrong</h2>
    }

    // Running balance + per-type voucher numbering (INV-001, RCPT-001, ...).
    let running = ledger?.broughtForward || 0;
    const counters = {};
    const rowsWithBal = (ledger?.rows || []).map((r) => {
        running = running + (r.debit || 0) - (r.credit || 0);
        const series = r.voucher === 'invoice' ? 'INV'
            : r.voucher === 'receipt' ? 'RCPT'
                : r.debit ? 'ADD' : 'DED';
        counters[series] = (counters[series] || 0) + 1;
        const voucherNo = `${series}-${String(counters[series]).padStart(3, '0')}`;
        return { ...r, balance: running, voucherNo };
    });
    const totalDebit = rowsWithBal.reduce((s, r) => s + (r.debit || 0), 0);
    const totalCredit = rowsWithBal.reduce((s, r) => s + (r.credit || 0), 0);

    function remarksTxt(r) {
        if (r.voucher === 'invoice')
            return r.custReqId || "";
        if (r.voucher === 'receipt') {
            const from = r.receivedFrom === 'consignee' ? ` (${t("bo.custLedgerPg.fromCnee")})`
                : r.receivedFrom === 'consignor' ? ` (${t("bo.custLedgerPg.fromCnor")})` : "";
            const txn = r.transactionId ? `, ${t("bo.custLedgerPg.txnId")}: ${r.transactionId}` : "";
            return `${t("bo.custLedgerPg.receiptBy")} ${formatPymtType(r.paymentType)}${from}${txn}`;
        }
        return `${BREAKUP_LABELS[r.voucher] || r.voucher}${r.voucherFreeTxt ? ` (${r.voucherFreeTxt})` : ""}`;
    }

    async function handleDownloadPdf() {
        setLoadingScreen(true);
        try {
            const partyName = custDDArr.find((c) => c.value === String(custId))?.label || "";
            const res = await custLedgerPdf({ custId, fromDate, toDate, partyName });
            window.open(res.fileUrl, "_blank");
        } catch (err) {
            customAlert(err);
        }
        setLoadingScreen(false);
    }

    const headCellSx = { fontWeight: "bold", backgroundColor: "#F6F5FB" };
    const amtCellSx = { textAlign: "right", whiteSpace: "nowrap" };

    return (
        <BgBox px={4} py={1}>
            <Stack direction={'row'} justifyContent="space-between" alignItems={"flex-end"} mb={1.5} mt={0.5} px={2}>
                <Typography variant='h5' sx={{ fontWeight: "bold" }}>{t("bo.custLedgerPg.title")}</Typography>

                <Stack direction={'row'} alignItems={"flex-end"} gap={2}>
                    <CustomDropDown title={t("bo.custLedgerPg.selCust")} height={36} width={220} fontSize={13}
                        handleDDChange={(e) => setParam("custId", e.target.value)} ddArr={custDDArr} defaultValue={custId} />
                    <CtrlOutTxtField value={fromDate} name={"fromDate"} onChange={(e) => setParam("fromDate", e.target.value)} title={t("bo.filterMdl.fromDate")} fontSize={13} height={35} width={150} type={"date"} />
                    <CtrlOutTxtField value={toDate} name={"toDate"} onChange={(e) => setParam("toDate", e.target.value)} title={t("bo.filterMdl.toDate")} fontSize={13} height={35} width={150} type={"date"} />
                    <Button variant='contained' sx={{ height: 36 }} startIcon={<PictureAsPdf />} color='primary' disabled={!canFetch} onClick={handleDownloadPdf}>{t("bo.reptListPg.pdf")}</Button>
                </Stack>
            </Stack>

            {rangeTooLong &&
                <Typography sx={{ color: "error.main", fontSize: 13, px: 2, mb: 1 }}>{t("bo.custLedgerPg.sixMonthErr")}</Typography>}

            {!custId ?
                <Stack alignItems="center" justifyContent="center" height="70%">
                    <Typography sx={{ color: "text.secondary" }}>{t("bo.custLedgerPg.pickCustMsg")}</Typography>
                </Stack>
                : (isLoading && canFetch) ?
                    <Box sx={{ position: "absolute", top: 0, left: 0 }}><LoadingScreen /></Box>
                    :
                    <StyledTableContainer height={"85%"}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headCellSx}>{t("bo.custLedgerPg.date")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.custLedgerPg.voucherNo")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.custLedgerPg.remarks")}</TableCell>
                                <TableCell sx={headCellSx}>{t("bo.custLedgerPg.shipmtId")}</TableCell>
                                <TableCell sx={{ ...headCellSx, ...amtCellSx }}>{t("bo.custLedgerPg.debit")}</TableCell>
                                <TableCell sx={{ ...headCellSx, ...amtCellSx }}>{t("bo.custLedgerPg.credit")}</TableCell>
                                <TableCell sx={{ ...headCellSx, ...amtCellSx }}>{t("bo.custLedgerPg.balance")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ backgroundColor: "#FBFAF4" }}>
                                <TableCell colSpan={6} sx={{ fontWeight: "600" }}>{t("bo.custLedgerPg.broughtFwd")}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "600" }}>{fmtBal(ledger?.broughtForward)}</TableCell>
                            </TableRow>
                            {rowsWithBal.map((r, idx) => (
                                <TableRow key={idx} hover>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.txnDate)}</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.voucherNo}</TableCell>
                                    <TableCell>{remarksTxt(r)}</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.shipmtId}</TableCell>
                                    <TableCell sx={amtCellSx}>{fmtAmt(r.debit)}</TableCell>
                                    <TableCell sx={amtCellSx}>{fmtAmt(r.credit)}</TableCell>
                                    <TableCell sx={amtCellSx}>{fmtBal(r.balance)}</TableCell>
                                </TableRow>
                            ))}
                            {rowsWithBal.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: "center", color: "text.secondary" }}>{t("bo.custLedgerPg.noTxns")}</TableCell>
                                </TableRow>}
                            <TableRow sx={{ backgroundColor: "#F6F5FB" }}>
                                <TableCell colSpan={4} sx={{ fontWeight: "bold" }}>{t("bo.custLedgerPg.total")}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "bold" }}>{fmtAmt(totalDebit)}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "bold" }}>{fmtAmt(totalCredit)}</TableCell>
                                <TableCell sx={{ ...amtCellSx, fontWeight: "bold" }}>{fmtBal((ledger?.broughtForward || 0) + totalDebit - totalCredit)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </StyledTableContainer>}
        </BgBox>
    )
}

export default CustLedgerPage;
